import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const ThemeContext = createContext({ branding: {}, theme: {}, menu: {}, reload: () => {}, dark: false, setDark: () => {} });

const DEFAULTS = {
  branding: { site_name: "Immobilier Solaire", site_slogan: "" },
  theme: {
    font_heading: "Archivo", font_body: "Outfit", font_mono: "JetBrains Mono",
    color_accent: "#FFC107", color_accent_hover: "#E5A910",
    color_ink: "#111111", color_bg: "#F9F9F7", color_surface: "#FFFFFF",
    text_size_preset: "normal", dark_mode_enabled: true,
  },
  menu: { header_links: [], footer_columns: [], socials: [] },
};

// Google Fonts loading
const loadedFonts = new Set();
function loadGoogleFont(family) {
  if (!family || loadedFonts.has(family)) return;
  loadedFonts.add(family);
  const id = `gf-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@300;400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
}

function setFavicon(url) {
  if (!url) return;
  let link = document.querySelector("link[rel*='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
}

export function ThemeProvider({ children }) {
  const [data, setData] = useState({ branding: DEFAULTS.branding, theme: DEFAULTS.theme, menu: DEFAULTS.menu });
  const [dark, setDarkState] = useState(() => localStorage.getItem("dark_mode") === "1");

  const reload = useCallback(async () => {
    try {
      const { data: all } = await api.get("/site-content");
      setData({
        branding: { ...DEFAULTS.branding, ...(all.branding || {}) },
        theme: { ...DEFAULTS.theme, ...(all.theme || {}) },
        menu: { ...DEFAULTS.menu, ...(all.menu || {}) },
      });
    } catch (err) { console.error("Site content load failed:", err); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Apply theme to CSS variables + load fonts + favicon + document title
  useEffect(() => {
    const t = data.theme || {};
    const b = data.branding || {};
    loadGoogleFont(t.font_heading);
    loadGoogleFont(t.font_body);
    loadGoogleFont(t.font_mono);
    const r = document.documentElement.style;
    r.setProperty("--solar", t.color_accent);
    r.setProperty("--solar-hover", t.color_accent_hover);
    r.setProperty("--bg-default", t.color_bg);
    r.setProperty("--bg-surface", t.color_surface);
    r.setProperty("--text-primary", t.color_ink);
    r.setProperty("--font-heading", `'${t.font_heading}', system-ui, sans-serif`);
    r.setProperty("--font-body", `'${t.font_body}', system-ui, sans-serif`);
    r.setProperty("--font-mono", `'${t.font_mono}', monospace`);
    document.body.style.fontFamily = `'${t.font_body}', system-ui, sans-serif`;
    setFavicon(b.favicon_url);
    // Only set default title if no page-specific title was set (BlogPost / DynamicPage set window.__pageTitleOverride)
    if (b.site_name && !window.__pageTitleOverride) {
      document.title = b.site_slogan ? `${b.site_name} — ${b.site_slogan}` : b.site_name;
    }
  }, [data]);

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("dark_mode", dark ? "1" : "0");
  }, [dark]);

  const setDark = (v) => setDarkState(v);

  return (
    <ThemeContext.Provider value={{ ...data, reload, dark, setDark }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
